import type {
  Bonus,
  ComparisonRow,
  Faq,
  ShortQuote,
  Stat,
  Testimonial,
  Track,
  Volume,
} from '../types/content';

export const OFFER_DEADLINE_MS = Date.now() + (11 * 3600 + 13 * 60 + 35) * 1000;

export const CHECKOUT_URL = 'https://el-profe-de-musica.impultienda.ar/checkout';

export const PRICE_LABEL = '$27.990';
export const PRICE_NUMERIC = 27990;

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/qyxmTQkf72Z45pzGzcoSSF/pasted-image.jpg';

export const heroBullets: string[] = [
  '6 volúmenes completos',
  '5 bonos exclusivos',
  '+300 páginas listas para imprimir',
  '+150 ejercicios y fichas',
  '100% PDF — enviado a tu email',
  'Todas las edades y niveles',
];

export const proofStats: Stat[] = [
  { value: '300+', label: 'Páginas PDF listas para imprimir' },
  { value: '6 + 5', label: 'Volúmenes y bonos incluidos' },
  { value: '50+', label: 'Planes de clase estructurados' },
  { value: 'Niños a adultos', label: 'De principiante a avanzado' },
];

export const shortQuotes: ShortQuote[] = [
  {
    quote:
      'Antes de tener este pack pasaba 5 horas por semana solo buscando qué enseñar. Ahora imprimo y enseño.',
    author: 'Sarah J. · Profesora de piano',
  },
  {
    quote:
      'Mis principiantes tocan música real en la tercera semana. Solo las fichas de rasgueo ya valían el precio.',
    author: 'Daniel M. · Profesor de guitarra',
  },
  {
    quote:
      'Todos los profes de mi academia trabajan con el mismo plan. Los padres finalmente ven el progreso.',
    author: 'Laura C. · Directora de academia',
  },
];

export const painPoints: string[] = [
  'Pasás horas buscando material en Pinterest y YouTube que parezca profesional, y solo encontrás cosas mediocres',
  'Repetís las mismas fichas porque ya te quedaste sin material nuevo',
  'No tenés una progresión lógica planificada para todo el año',
  'Perdés alumnos después del segundo mes porque se aburren con métodos viejos y polvorientos',
  'Llegás sin preparación y con estrés a cada clase',
  'Sos un gran músico, pero estás agotado de improvisar cada clase',
];

export const systemStats: Stat[] = [
  { value: '300+', label: 'Páginas PDF prácticas' },
  { value: '150+', label: 'Ejercicios listos para enseñar' },
  { value: '50+', label: 'Planes de clase completos' },
  { value: '6', label: 'Volúmenes completos' },
];

export const tracks: Track[] = [
  { number: '01', label: 'Bases para principiantes' },
  { number: '02', label: 'Repertorio intermedio' },
  { number: '03', label: 'Técnica avanzada' },
  { number: '04', label: 'Rutas de piano y guitarra' },
  { number: '05', label: 'Teoría, lectura y juegos' },
  { number: '06', label: 'Evaluación y seguimiento' },
];

export const volumes: Volume[] = [
  {
    number: '01',
    pages: '50+ páginas PDF',
    title: 'Bases de piano',
    description:
      'Desde «La araña de ébano» en adelante: los ejercicios que hacen que un principiante absoluto toque música reconocible en tiempo récord.',
    tags: ['Primeras notas', 'La araña de ébano', 'Patrones de cinco dedos', 'Lectura a primera vista'],
  },
  {
    number: '02',
    pages: '50+ páginas PDF',
    title: 'Dominio del piano e independencia de manos',
    description:
      'La mitad intermedia de la serie de piano: ejercicios que construyen coordinación, dinámica e independencia avanzada de manos.',
    tags: ['Independencia de manos', 'Escalas y arpegios', 'Dinámica', 'Repertorio'],
  },
  {
    number: '03',
    pages: '50+ páginas PDF',
    title: 'Bases del mástil en guitarra',
    description:
      'Dominá el mástil desde la primera clase: acordes abiertos, power chords y escalas que de verdad tienen sentido para un principiante.',
    tags: ['Acordes abiertos', 'Power chords', 'Mapas del mástil', 'Escalas para principiantes'],
  },
  {
    number: '04',
    pages: '50+ páginas PDF',
    title: 'Ritmo y performance en guitarra',
    description:
      'Fichas con el «patrón de rasgueo latido», riffs y canciones listas para tocar en solitario o en grupo.',
    tags: ['Rasgueo latido', 'Riffs', 'Fichas de canciones', 'Preparación para tocar'],
  },
  {
    number: '05',
    pages: '50+ páginas PDF',
    title: 'Teoría aplicada y lectura',
    description:
      'Se terminaron las clases aburridas. Cifrado americano visual, juegos de dictado rítmico y fichas «Sopa de notas» que hacen divertida la lectura.',
    tags: ['Cifrado americano', 'Dictado rítmico', 'Sopa de notas', 'Juegos de teoría'],
  },
  {
    number: '06',
    pages: '50+ páginas PDF',
    title: 'El manual de operaciones del profe',
    description:
      'Tu año, planificado. Más de 50 planes de clase, rúbricas de evaluación y el «Plan Rockstar de 30 días» para que cada alumno se sienta un ganador.',
    tags: ['+50 planes de clase', 'Rúbricas', 'Plan Rockstar 30 días', 'Informes para padres'],
  },
];

export const bonuses: Bonus[] = [
  {
    number: '01',
    value: '$97.000 · Gratis',
    title: 'Diagnóstico Express del Alumno en 10 Minutos',
    description:
      'Una ficha imprimible para detectar en la primera clase el nivel real, los vicios de técnica y los objetivos de cualquier alumno nuevo — en solo 10 minutos.',
    cover: 'https://cdn.magicpatterns.com/uploads/siSKS5iFmizZjgRgLfcP7Q/guitara_1.webp',
  },
  {
    number: '02',
    value: '$97.000 · Gratis',
    title: 'Tracker de Progreso BPM y Tareas Semanales',
    description:
      'Planillas imprimibles para registrar el BPM de cada ejercicio y las tareas de la semana, para que el alumno vea su progreso en números.',
    cover: 'https://cdn.magicpatterns.com/uploads/ueNdadiXoZT7s4kjG3yKjX/guitarra_3.webp',
  },
  {
    number: '03',
    value: '$127.000 · Gratis',
    title: 'Biblioteca de Calentamientos Rápidos para Cada Clase',
    description:
      'Más de 30 calentamientos de 5 minutos para dedos, muñeca y púa, listos para abrir cualquier clase sin pensarlo.',
    cover: 'https://cdn.magicpatterns.com/uploads/bTWYDzBE47mNp6E1aRUF4v/guitarra_2.webp',
  },
  {
    number: '04',
    value: '$47.000 · Gratis',
    title: '50 Retos Rápidos para Motivar al Alumno',
    description:
      '50 retos cortos e imprimibles que convierten la práctica en casa en un juego y hacen que el alumno vuelva con algo listo.',
    cover: 'https://cdn.magicpatterns.com/uploads/gURHZU838xWP2t1JfawLAu/guitara_4.webp',
  },
  {
    number: '05',
    value: '$150.000 · Gratis',
    title: 'Guía de Correcciones Rápidas para Profesores de Guitarra',
    description:
      'Los errores más comunes de postura, mano izquierda y ritmo, con la corrección exacta que funciona en el momento.',
    cover: 'https://cdn.magicpatterns.com/uploads/n3G9W3j11HJSwP1hNJX9Bu/guitarra_5.webp',
  },
];

export const offerIncludes: string[] = [
  '6 volúmenes completos de enseñanza',
  '5 guías bonus exclusivas — gratis',
  '+300 páginas listas para imprimir',
  '+50 planes de clase estructurados',
  '100% PDF — sin videos ni audiolibros',
  'Enviado a tu email justo después de comprar',
];

export const testimonials: Testimonial[] = [
  {
    initials: 'SJ',
    name: 'Sarah J.',
    role: 'Profesora de piano',
    quote:
      'Antes de tener este pack pasaba 5 horas por semana solo buscando qué enseñar. Ahora imprimo y enseño. Mis alumnos progresan más rápido y no perdí un solo alumno en 3 meses.',
  },
  {
    initials: 'DM',
    name: 'Daniel M.',
    role: 'Profesor de guitarra',
    quote:
      'Las fichas de rasgueo y power chords hicieron que mis principiantes toquen canciones reales en la tercera semana. Nada de lo que compré antes logró eso.',
  },
  {
    initials: 'LC',
    name: 'Laura C.',
    role: 'Directora de academia de música',
    quote:
      'Todos los profes de mi academia trabajan ahora con el mismo material. Solo la licencia de impresión ya lo hizo una decisión obvia.',
  },
  {
    initials: 'RT',
    name: 'Rachel T.',
    role: 'Dueña de estudio particular',
    quote:
      'Las rúbricas de evaluación cambiaron mis reuniones con los padres. Puedo mostrar progreso concreto en vez de decir «va bien».',
  },
  {
    initials: 'MO',
    name: 'Marco O.',
    role: 'Profesor de piano y guitarra',
    quote:
      'Enseño los dos instrumentos, así que tener piano y guitarra en un solo sistema hace que mi planificación del domingo se resuelva en minutos.',
  },
];

export const faqs: Faq[] = [
  {
    question: '¿Es un libro físico? ¿En qué formato viene?',
    answer:
      'Es 100% digital y 100% PDF: no hay videos ni audiolibros. Cada volumen y cada bono es un PDF listo para imprimir que recibís por email, así que podés descargar e imprimir todo de inmediato.',
  },
  {
    question: '¿Funciona para todas las edades?',
    answer:
      'Sí. El material cubre desde niños que recién empiezan hasta adolescentes y adultos en nivel avanzado, con progresiones separadas para cada etapa.',
  },
  {
    question: '¿Puedo usarlo en mi academia de música?',
    answer:
      'Sí. Incluye licencia de impresión ilimitada para tu estudio o academia: todos tus profes pueden trabajar con el mismo material y las mismas planificaciones.',
  },
  {
    question: '¿Cómo recibo el material?',
    answer:
      'Apenas completás la compra te llega un email con los enlaces de descarga de los 6 volúmenes y los 5 bonos. No hay plataforma para loguearse ni esperas.',
  },
  {
    question: '¿Y si no quedo del todo satisfecho?',
    answer:
      'Tenés 7 días de garantía. Escribinos un email y te devolvemos el 100% de lo que pagaste, sin formularios y sin preguntas. Los bonos te los quedás.',
  },
];

export const comparison: ComparisonRow[] = [
  {
    without: '5+ horas por semana buscando fichas',
    with: 'Imprimí y enseñá · Todo ya preparado',
  },
  {
    without: 'Clases improvisadas · Sin progresión clara',
    with: '+300 páginas PDF · Progresión lógica semana a semana',
  },
  {
    without: 'Alumnos aburridos con métodos polvorientos',
    with: 'Pistas, juegos y canciones que ellos te piden',
  },
  {
    without: 'Padres preguntando «¿dónde están los resultados?»',
    with: 'Rúbricas, diplomas e informes de progreso listos',
  },
  {
    without: 'Domingos perdidos maquetando fichas',
    with: 'Todo tu año planificado · Domingos recuperados',
  },
];
